import { Icon } from '@iconify/react'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Rnd } from 'react-rnd'

import { useAuth } from 'app/providers/auth/AuthProvider'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import { closeTelehealthSession } from '../state/jitsiTelehealth.slice'

import styles from './jitsiTelehealthFloatingDialog.module.scss'

const DEFAULT_WIDTH = 480
const DEFAULT_HEIGHT = 360
const MIN_WIDTH = 320
const MIN_HEIGHT = 240
const MINIMIZED_HEIGHT = 44
const WINDOW_MARGIN = 24
const CONNECT_TIMEOUT_MS = 15000
const LAYOUT_STORAGE_KEY = 'jitsiTelehealth:dialogLayout'

const RESIZE_HANDLE_STYLES = {
  topLeft: { width: '20px', height: '20px', left: '-4px', top: '-4px' },
  topRight: { width: '20px', height: '20px', right: '-4px', top: '-4px' },
  bottomLeft: { width: '20px', height: '20px', left: '-4px', bottom: '-4px' },
  bottomRight: { width: '28px', height: '28px', right: '-2px', bottom: '-2px' },
  top: { height: '12px', top: '-6px' },
  bottom: { height: '12px', bottom: '-6px' },
  left: { width: '12px', left: '-6px' },
  right: { width: '12px', right: '-6px' },
} as const

type Position = {
  x: number
  y: number
}

type Size = {
  width: number
  height: number
}

type PersistedLayout = {
  position: Position
  size: Size
  isMinimized: boolean
  isMaximized: boolean
}

const computeInitialPosition = (): Position => ({
  x: Math.max(WINDOW_MARGIN, window.innerWidth - DEFAULT_WIDTH - WINDOW_MARGIN),
  y: Math.max(WINDOW_MARGIN, window.innerHeight - DEFAULT_HEIGHT - WINDOW_MARGIN),
})

const readPersistedLayout = (): Partial<PersistedLayout> => {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (!raw) {
      return {}
    }
    return JSON.parse(raw) as Partial<PersistedLayout>
  } catch {
    return {}
  }
}

const writePersistedLayout = (layout: PersistedLayout): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout))
  } catch {
    // localStorage may fail (quota / private mode) — fail silently
  }
}

const getViewportSize = (): Size => ({
  width: typeof window !== 'undefined' ? window.innerWidth : DEFAULT_WIDTH,
  height: typeof window !== 'undefined' ? window.innerHeight : DEFAULT_HEIGHT,
})

export const JitsiTelehealthFloatingDialog = () => {
  const { isDoctor } = useAuth()
  const activeSession = useAppSelector((state) => state.jitsiTelehealthReducer.activeSession)
  const dispatch = useAppDispatch()

  const [position, setPosition] = useState<Position>(() => {
    const persisted = readPersistedLayout()
    return persisted.position ?? computeInitialPosition()
  })
  const [size, setSize] = useState<Size>(() => {
    const persisted = readPersistedLayout()
    return persisted.size ?? { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
  })
  const [isMinimized, setIsMinimized] = useState<boolean>(
    () => readPersistedLayout().isMinimized ?? false,
  )
  const [isMaximized, setIsMaximized] = useState<boolean>(
    () => readPersistedLayout().isMaximized ?? false,
  )
  const [viewport, setViewport] = useState<Size>(getViewportSize)
  const shieldRef = useRef<HTMLDivElement>(null)

  const armDragShield = () => {
    if (shieldRef.current) {
      shieldRef.current.style.pointerEvents = 'auto'
    }
  }

  const disarmDragShield = () => {
    if (shieldRef.current) {
      shieldRef.current.style.pointerEvents = 'none'
    }
  }
  const [isConnecting, setIsConnecting] = useState(true)
  const [hasConnectionError, setHasConnectionError] = useState(false)

  const isActive = activeSession !== null
  const embedUrl = activeSession?.doctor_embed_url ?? null
  const patientName = activeSession?.patientName?.trim() ?? ''
  const headerTitle = patientName.length > 0 ? `Call with ${patientName}` : 'Telehealth call'

  const callStatus: 'connecting' | 'connected' | 'error' = hasConnectionError
    ? 'error'
    : isConnecting
    ? 'connecting'
    : 'connected'

  useEffect(() => {
    if (!isActive) {
      return
    }
    setIsConnecting(true)
    setHasConnectionError(false)
  }, [activeSession?.appointmentId, isActive])

  useEffect(() => {
    if (!isActive) {
      return
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isActive])

  useEffect(() => {
    if (!isActive || !isConnecting) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      setHasConnectionError(true)
      setIsConnecting(false)
    }, CONNECT_TIMEOUT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isActive, isConnecting, embedUrl])

  useEffect(() => {
    if (!isMaximized) {
      return
    }
    const handler = () => setViewport(getViewportSize())
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [isMaximized])

  useEffect(() => {
    writePersistedLayout({ position, size, isMinimized, isMaximized })
  }, [position, size, isMinimized, isMaximized])

  useEffect(() => {
    if (!isActive) {
      return
    }
    const release = () => disarmDragShield()
    window.addEventListener('mouseup', release, true)
    window.addEventListener('touchend', release, true)
    window.addEventListener('blur', release)
    return () => {
      window.removeEventListener('mouseup', release, true)
      window.removeEventListener('touchend', release, true)
      window.removeEventListener('blur', release)
    }
  }, [isActive])

  if (!isDoctor || !activeSession) {
    return null
  }

  const handleClose = () => dispatch(closeTelehealthSession())

  const handleToggleMinimize = () => {
    setIsMinimized((prev) => {
      const next = !prev
      if (next) {
        setIsMaximized(false)
      }
      return next
    })
  }

  const handleToggleMaximize = () => {
    setIsMaximized((prev) => {
      const next = !prev
      if (next) {
        setIsMinimized(false)
      }
      return next
    })
  }

  const effectivePosition = isMaximized ? { x: 0, y: 0 } : position
  const effectiveSize = isMaximized
    ? viewport
    : { width: size.width, height: isMinimized ? MINIMIZED_HEIGHT : size.height }

  const minimizeIcon = isMinimized ? 'fluent:square-24-regular' : 'fluent:subtract-24-regular'
  const maximizeIcon = isMaximized
    ? 'fluent:full-screen-minimize-24-regular'
    : 'fluent:full-screen-maximize-24-regular'

  return createPortal(
    <Rnd
      className={styles.shell}
      position={effectivePosition}
      size={effectiveSize}
      minWidth={MIN_WIDTH}
      minHeight={isMinimized ? MINIMIZED_HEIGHT : MIN_HEIGHT}
      bounds='window'
      dragHandleClassName={styles.parentHeader}
      cancel={`.${styles.parentActions}`}
      disableDragging={isMaximized}
      enableResizing={!isMinimized && !isMaximized}
      resizeHandleStyles={RESIZE_HANDLE_STYLES}
      resizeHandleComponent={{
        bottomRight: <div className={styles.resizeGripper} aria-hidden='true' />,
      }}
      onDragStart={armDragShield}
      onDragStop={(_event, data) => {
        disarmDragShield()
        setPosition({ x: data.x, y: data.y })
      }}
      onResizeStart={armDragShield}
      onResizeStop={(_event, _direction, ref, _delta, nextPosition) => {
        disarmDragShield()
        setSize({ width: ref.offsetWidth, height: ref.offsetHeight })
        setPosition(nextPosition)
      }}>
      <div
        role='dialog'
        aria-modal='false'
        aria-label={headerTitle}
        className={classNames(styles.parent, {
          [styles.minimized]: isMinimized,
          [styles.maximized]: isMaximized,
        })}>
        <div className={styles.parentHeader}>
          <div className={styles.parentTitle}>
            <Icon icon='fluent:video-person-call-24-regular' fontSize={18} />
            <span
              className={classNames(styles.parentStatusDot, {
                [styles.parentStatusDotConnecting]: callStatus === 'connecting',
                [styles.parentStatusDotError]: callStatus === 'error',
              })}
              aria-hidden='true'
            />
            <span>{headerTitle}</span>
          </div>
          <div className={styles.parentActions}>
            <button
              type='button'
              className={styles.parentIconButton}
              aria-label={isMinimized ? 'Restore call window' : 'Minimize call window'}
              onClick={handleToggleMinimize}>
              <Icon icon={minimizeIcon} fontSize={16} />
            </button>
            <button
              type='button'
              className={styles.parentIconButton}
              aria-label={isMaximized ? 'Restore call window size' : 'Maximize call window'}
              onClick={handleToggleMaximize}>
              <Icon icon={maximizeIcon} fontSize={16} />
            </button>
            <button
              type='button'
              className={styles.parentIconButton}
              aria-label='End telehealth call'
              title='End telehealth call'
              onClick={handleClose}>
              <Icon icon='fluent:dismiss-24-regular' fontSize={16} />
            </button>
          </div>
        </div>
        <div className={styles.parentBody}>
          {callStatus === 'connecting' && (
            <div className={styles.parentStatus} role='status'>
              Connecting to call…
            </div>
          )}
          {callStatus === 'error' && (
            <div className={styles.parentStatus} role='alert'>
              Unable to connect to the call. Close this window and try again.
            </div>
          )}
          <iframe
            key={activeSession.appointmentId}
            className={styles.parentIframe}
            title={headerTitle}
            src={embedUrl ?? ''}
            referrerPolicy='strict-origin-when-cross-origin'
            allow='camera *; microphone *; fullscreen *; display-capture *; autoplay *; clipboard-write *'
            allowFullScreen
            onLoad={() => {
              setIsConnecting(false)
              setHasConnectionError(false)
            }}
            onError={() => {
              setHasConnectionError(true)
              setIsConnecting(false)
            }}
          />
          <div
            ref={shieldRef}
            className={styles.parentDragShield}
            style={{ pointerEvents: 'none' }}
            aria-hidden='true'
          />
        </div>
      </div>
    </Rnd>,
    document.body,
  )
}
